/**
 * store.ts — a tiny observable holder for the active locale.
 *
 * Plain observer pattern, dependency-free: enough for a web-kit signal or a
 * React adapter to wrap. No persistence, no `localStorage`, no DOM — those are
 * adapter concerns layered on top in the consuming package.
 */
import type { Locale } from "./types.ts";

export interface LocaleStore {
  /** The current active locale. */
  get(): Locale;
  /** Set the active locale; notifies subscribers only when it actually changes. */
  set(locale: Locale): void;
  /** Subscribe to locale changes. Returns an unsubscribe function. */
  subscribe(listener: (locale: Locale) => void): () => void;
}

export function createLocaleStore(initial: Locale): LocaleStore {
  let current = initial;
  const listeners = new Set<(locale: Locale) => void>();
  return {
    get: () => current,
    set(locale) {
      if (locale === current) return;
      current = locale;
      for (const listener of listeners) listener(locale);
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
